import React, { FC, useCallback } from 'react';
import {
  Divider,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import {
  EventCategoryEnergyPreference,
  EventCategoryEnergyPreferenceLevel,
} from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  usePreferences,
  useUpdateEventCategoryEnergyPref,
} from '../../../../../data-access';
import { eventCategoriesForEnergyPref, energyPrefLevelText } from '../../../../Preference';
import { defaultEnergyPref } from '../../../../SideBar/SettingsModal/EnergyPrefSettings/constants';
import { ReactComponent as CaretUpDownIcon } from '../../../../../assets/tabs/settings-tab/caret-up-down-icon.svg';
import './EnergyPreferencesSection.scss';

interface SelectIconProps {
  className?: string;
}

const EnergySelectIcon: React.FC<SelectIconProps> = ({ className }) => {
  return <CaretUpDownIcon className={className} />;
};

const EnergyPreferencesSection: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { energyPreferences } = usePreferences({ preferenceId: user.preferenceId! });
  const { updateEventCategoryEnergyPref } = useUpdateEventCategoryEnergyPref();

  const handleChange = useCallback(
    async (categoryId: string, categoryName: string, level: EventCategoryEnergyPreferenceLevel) => {
      const preference: EventCategoryEnergyPreference = {
        categoryId,
        categoryName,
        level,
      };

      await updateEventCategoryEnergyPref({
        preferenceId: user.preferenceId!,
        preferences: [preference],
      });
    },
    [updateEventCategoryEnergyPref, user.preferenceId]
  );

  return (
    <Stack gap={2} className="energy-preferences-section">
      <Typography variant="h3" className="energy-preferences-section__title">
        {t('settings.nav.energyPreferences')}
      </Typography>

      <div className="energy-preferences-section__block">
        <Stack className="energy-preferences-section__list" gap={2}>
          {eventCategoriesForEnergyPref.map((category, index) => {
            const existingPreference = energyPreferences.eventCategories?.find(
              ({ categoryId }) => categoryId === category.id
            );

            const selectedLevel =
              existingPreference?.level || defaultEnergyPref[category.id] || 'more_energized';

            return (
              <Stack
                key={category.id}
                className="energy-preferences-section__list-item-wrapper"
                gap={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  className="energy-preferences-section__list-item"
                >
                  <Typography
                    variant="body1"
                    className="energy-preferences-section__list-item-title"
                  >
                    {t(`settings.energyPreferences.categories.${category.id}`)}
                  </Typography>

                  <FormControl size="small" className="energy-preferences-section__select-control">
                    <Select
                      value={selectedLevel}
                      onChange={(event: SelectChangeEvent) =>
                        void handleChange(
                          category.id,
                          category.name,
                          event.target.value as EventCategoryEnergyPreferenceLevel
                        )
                      }
                      className="energy-preferences-section__select"
                      IconComponent={EnergySelectIcon}
                      MenuProps={{
                        PaperProps: {
                          className: 'energy-preferences-section__menu-paper',
                        },
                        MenuListProps: {
                          className: 'energy-preferences-section__menu-list',
                        },
                      }}
                    >
                      {Object.keys(energyPrefLevelText).map((key) => (
                        <MenuItem
                          key={key}
                          value={key}
                          className="energy-preferences-section__menu-item"
                        >
                          {t(`settings.energyPreferences.levels.${key}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {index !== eventCategoriesForEnergyPref.length - 1 && (
                  <Divider className="energy-preferences-section__list-item-divider" />
                )}
              </Stack>
            );
          })}
        </Stack>
      </div>
    </Stack>
  );
};

export default EnergyPreferencesSection;
